import { Card, CardContent } from "@/components/ui/card";
import { Trophy } from "lucide-react";

export function IndividualsTab({ round, isVuelta1 }) {
  return (
    <>
      <Card className="card-default mb-4">
        <CardContent className="p-4">
          <h3 className="text-lg font-bold text-[#0F172A] mb-4">Resumen por Jugador</h3>
          
          <div className="space-y-3 mb-6">
            {(() => {
              const playerBalances = {};
              round?.players?.forEach(p => {
                playerBalances[p.player_id] = { name: p.name, won: 0, lost: 0, balance: 0 };
              });
              
              round?.individual_matchups?.forEach(m => {
                if (m.total_points > 0) {
                  playerBalances[m.player1_id].won += m.total_points;
                  playerBalances[m.player2_id].lost += m.total_points;
                } else if (m.total_points < 0) {
                  playerBalances[m.player2_id].won += Math.abs(m.total_points);
                  playerBalances[m.player1_id].lost += Math.abs(m.total_points);
                }
              });
              
              Object.keys(playerBalances).forEach(id => {
                playerBalances[id].balance = playerBalances[id].won - playerBalances[id].lost;
              });
              
              const sortedPlayers = Object.entries(playerBalances)
                .sort((a, b) => b[1].balance - a[1].balance);
              
              return sortedPlayers.map(([playerId, data], index) => (
                <div 
                  key={playerId}
                  className={`flex items-center justify-between p-3 rounded-lg ${
                    index === 0 && data.balance > 0 ? 'bg-green-50 border border-green-200' : 'bg-gray-50'
                  }`}
                >
                  <div className="flex items-center gap-2">
                    {index === 0 && data.balance > 0 && (
                      <Trophy className="w-5 h-5 text-green-600" />
                    )}
                    <span className="font-semibold text-[#0F172A] truncate max-w-[120px] sm:max-w-none" title={data.name}>{data.name}</span>
                  </div>
                  <div className="flex items-center gap-4 text-sm">
                    <span className="text-green-600">+{data.won}</span>
                    <span className="text-red-600">-{data.lost}</span>
                    <span className={`text-lg font-bold ${
                      data.balance > 0 ? 'text-green-600' : 
                      data.balance < 0 ? 'text-red-600' : 'text-gray-500'
                    }`}>
                      = {data.balance > 0 ? '+' : ''}{data.balance}
                    </span>
                  </div>
                </div>
              ));
            })()}
          </div>
        </CardContent>
      </Card>

      <h4 className="text-base font-bold text-[#0F172A] mb-3">Detalle de Enfrentamientos</h4>
      <div className="space-y-3">
        {round?.individual_matchups?.map((matchup, index) => (
          <Card key={matchup.matchup_id || index} className="card-default">
            <CardContent className="p-4">
              <div className="flex items-center justify-between mb-3">
                <div>
                  <p className="font-semibold text-[#0F172A] text-sm sm:text-base">
                    <span className="truncate inline-block max-w-[100px] sm:max-w-none align-bottom" title={matchup.player1_name}>{matchup.player1_name}</span>
                    {' vs '}
                    <span className="truncate inline-block max-w-[100px] sm:max-w-none align-bottom" title={matchup.player2_name}>{matchup.player2_name}</span>
                  </p>
                  <p className="text-xs text-[#64748B]">
                    Ventaja: {matchup.handicap_difference} golpes a {
                      matchup.advantage_to === 'player1' ? matchup.player1_name?.split(' ')[0] : 
                      matchup.advantage_to === 'player2' ? matchup.player2_name?.split(' ')[0] : 
                      'ninguno'
                    }
                  </p>
                  <div className="flex items-center gap-2 mt-1 text-xs text-[#64748B]">
                    <span>V1: <span className={`font-semibold ${(matchup.vuelta1_points || 0) > 0 ? 'text-green-600' : (matchup.vuelta1_points || 0) < 0 ? 'text-red-600' : ''}`}>
                      {(matchup.vuelta1_points || 0) > 0 ? '+' : ''}{matchup.vuelta1_points || 0}
                    </span></span>
                    <span>V2: <span className={`font-semibold ${(matchup.vuelta2_points || 0) > 0 ? 'text-green-600' : (matchup.vuelta2_points || 0) < 0 ? 'text-red-600' : ''}`}>
                      {(matchup.vuelta2_points || 0) > 0 ? '+' : ''}{matchup.vuelta2_points || 0}
                    </span></span>
                  </div>
                </div>
                <div className={`px-3 py-1 rounded-lg ${
                  matchup.total_points > 0 ? 'bg-green-50' : 
                  matchup.total_points < 0 ? 'bg-red-50' : 'bg-gray-50'
                }`}>
                  <p className={`text-xl font-bold ${
                    matchup.total_points > 0 ? 'text-green-600' : 
                    matchup.total_points < 0 ? 'text-red-600' : 'text-gray-500'
                  }`}>
                    {matchup.total_points > 0 ? '+' : ''}{matchup.total_points}
                  </p>
                </div>
              </div>
              
              {Object.keys(matchup.hole_results || {}).length > 0 && (
                <div className="space-y-2">
                  {[1, 2].map(vuelta => {
                    const vHoles = Object.entries(matchup.hole_results || {}).filter(([hole]) => 
                      vuelta === 1 ? isVuelta1(parseInt(hole)) : !isVuelta1(parseInt(hole))
                    );
                    if (vHoles.length === 0) return null;
                    return (
                      <div key={vuelta}>
                        <p className="text-[10px] font-semibold text-[#64748B] mb-1">{vuelta === 1 ? '1ra' : '2da'} Vuelta</p>
                        <div className="flex flex-wrap gap-1">
                          {vHoles.map(([hole, result]) => (
                            <div 
                              key={hole}
                              className={`w-7 h-7 rounded text-xs font-semibold flex items-center justify-center ${
                                result.hole_points > 0 ? 'bg-green-100 text-green-700' : 
                                result.hole_points < 0 ? 'bg-red-100 text-red-700' : 
                                'bg-gray-100 text-gray-500'
                              }`}
                              title={`Hoyo ${hole}: ${matchup.player1_name?.split(' ')[0]} ${result.p1_net} vs ${matchup.player2_name?.split(' ')[0]} ${result.p2_net}`}
                            >
                              {result.hole_points > 0 ? '+' + result.hole_points : result.hole_points < 0 ? result.hole_points : '0'}
                            </div>
                          ))}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </CardContent>
          </Card>
        ))}
      </div>
    </>
  );
}
